import { DataAccess, ResponseMessage } from "../../../dataaccess/data.access";
import { S25Util } from "../../../util/s25-util";
import { S25Date } from "../../../util/core/s25.date";
import { TaskService } from "../../../services/task/task.service";
import { Task } from "../../../pojo/Task";
import { Workflow } from "./Workflow";
import { Cache } from "../../../decorators/cache.decorator";
import { S25Datefilter } from "../../s25-dateformat/s25.datefilter.service";
import { S25Dateformat } from "../../s25-dateformat/s25.dateformat.service";
import { AnswerType } from "../../data-ingestion/data.ingestion.service";
import { FieldType } from "../../data-ingestion/data.ingestion.const";
import { UserprefService } from "../../../services/userpref.service";
import { AlertService } from "../../../services/alert.service";
export class WorkflowService {
    /**
     * Available system actions with their input definitions
     */
    public static readonly SYSTEM_ACTIONS = {
        SEND_EMAIL: {
            actionCode: "SEND_EMAIL",
            displayName: "Send Email",
            description: "Send an email notification",
            inputs: [
                {
                    inputCode: "subject",
                    label: "Subject",
                    required: true,
                    type: "text",
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "body",
                    label: "Body",
                    required: true,
                    type: "richtext",
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "to",
                    label: "To",
                    required: true,
                    type: "text",
                    help: "Comma-separated email addresses. Special recipients: 'submitter', 'stage approvers', 'all approvers'",
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "from",
                    label: "From",
                    required: false,
                    type: "text",
                    help: "Email address (optional)",
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
            ],
        },
        CREATE_EVENT: {
            actionCode: "CREATE_EVENT",
            displayName: "Create Event",
            description: "Create a new event from form data",
            inputs: [
                {
                    inputCode: "eventName",
                    label: "Event Name",
                    type: "text",
                    required: true,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "startDate",
                    label: "Start Date",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.DATETIME],
                },
                {
                    inputCode: "endDate",
                    label: "End Date",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.DATETIME],
                },
                {
                    inputCode: "primaryOrgId",
                    label: "Primary Organization ID",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.INTEGER],
                    acceptedFieldTypes: ["ORGANIZATION", "TEXT"] as FieldType[],
                },
                {
                    inputCode: "eventTitle",
                    label: "Event Title",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "eventDescription",
                    label: "Event Description",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "internalNotes",
                    label: "Internal Notes",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "eventComment",
                    label: "Comment",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "confirmationText",
                    label: "Confirmation Text",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "expectedHeadcount",
                    label: "Expected Headcount",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.INTEGER],
                },
                {
                    inputCode: "preEventDuration",
                    label: "Pre-Event Time",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.DATETIME],
                },
                {
                    inputCode: "postEventDuration",
                    label: "Post-Event Time",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.DATETIME],
                },
                {
                    inputCode: "setupDuration",
                    label: "Setup Time",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.DATETIME],
                },
                {
                    inputCode: "takedownDuration",
                    label: "Takedown Time",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.DATETIME],
                },
                {
                    inputCode: "useAssignmentPolicy",
                    label: "Use Assignment Policy",
                    type: "checkbox",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.BOOLEAN],
                },
                {
                    inputCode: "enableRegistration",
                    label: "Enable Registration",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.BOOLEAN],
                },
                {
                    inputCode: "registrationFormId",
                    label: "Registration Form ID",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.INTEGER],
                },
                {
                    inputCode: "registrationStart",
                    label: "Registration Start Date",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.DATETIME],
                },
                {
                    inputCode: "registrationEnd",
                    label: "Registration End Date",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.DATETIME],
                },
                {
                    inputCode: "registrants",
                    label: "Who Can Register",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.JSON],
                },
                {
                    inputCode: "sendSurvey",
                    label: "Add Post-Event Survey",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.BOOLEAN],
                },
                {
                    inputCode: "surveyFormId",
                    label: "Survey Form ID",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.INTEGER],
                },
                {
                    inputCode: "surveyOffsetDays",
                    label: "Days After Event End",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.INTEGER],
                },
            ],
        },
        EDIT_EVENT: {
            actionCode: "EDIT_EVENT",
            displayName: "Edit Event",
            description:
                "Update an existing event from form data (registration and survey are not supported on edit; assignment policy applies when OBJECT_OCCURRENCE is submitted)",
            inputs: [
                {
                    inputCode: "eventIdField",
                    label: "Event ID Field",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.INTEGER],
                    acceptedFieldTypes: ["TEXT"] as FieldType[],
                },
                {
                    inputCode: "eventName",
                    label: "Event Name",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "startDate",
                    label: "Start Date",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.DATETIME],
                },
                {
                    inputCode: "endDate",
                    label: "End Date",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.DATETIME],
                },
                {
                    inputCode: "primaryOrgId",
                    label: "Primary Organization ID",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.INTEGER],
                    acceptedFieldTypes: ["ORGANIZATION", "TEXT"] as FieldType[],
                },
                {
                    inputCode: "eventTitle",
                    label: "Event Title",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "eventDescription",
                    label: "Event Description",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "internalNotes",
                    label: "Internal Notes",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "eventComment",
                    label: "Comment",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "confirmationText",
                    label: "Confirmation Text",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "expectedHeadcount",
                    label: "Expected Headcount",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.INTEGER],
                },
                {
                    inputCode: "preEventDuration",
                    label: "Pre-Event Time",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.DATETIME],
                },
                {
                    inputCode: "postEventDuration",
                    label: "Post-Event Time",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.DATETIME],
                },
                {
                    inputCode: "setupDuration",
                    label: "Setup Time",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.DATETIME],
                },
                {
                    inputCode: "takedownDuration",
                    label: "Takedown Time",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.DATETIME],
                },
                {
                    inputCode: "useAssignmentPolicy",
                    label: "Use Assignment Policy",
                    type: "checkbox",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.BOOLEAN],
                },
            ],
        },
        SPAWN_WORKFLOW: {
            actionCode: "SPAWN_WORKFLOW",
            displayName: "Spawn Form",
            description: "Spawn a child form for the submitter or contacts",
        },
        REGISTER: {
            actionCode: "REGISTER",
            displayName: "Add Registrant",
            description: "Register submitter for an event",
            inputs: [
                {
                    inputCode: "occurrences",
                    label: "Occurrences",
                    type: "text",
                    required: true,
                    acceptedAnswerTypes: [AnswerType.JSON],
                    acceptedFieldTypes: ["OBJECT_OCCURRENCE"] as FieldType[],
                },
            ],
        },
        SET_ORG_STATUS: {
            actionCode: "SET_ORG_STATUS",
            displayName: "Set Organization Status",
            description: "Set the organization rating/status",
            inputs: [
                {
                    inputCode: "organizationIdField",
                    label: "Organization ID Field",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.INTEGER],
                    acceptedFieldTypes: ["ORGANIZATION", "TEXT"] as FieldType[],
                },
            ],
        },
        SET_SECURITY_GROUP: {
            actionCode: "SET_SECURITY_GROUP",
            displayName: "Set Security Group",
            description:
                "Replace the form submitter's 25Live security group from a Security Group form answer, or from the first matching rule",
            inputs: [] as Workflow.SystemActionInput[],
        },
        SET_CONTACT_ROLE: {
            actionCode: "SET_CONTACT_ROLE",
            displayName: "Set Contact Role",
            description: "Set a role on a contact for an organization",
            inputs: [
                {
                    inputCode: "organizationIdField",
                    label: "Organization ID Field",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.INTEGER],
                    acceptedFieldTypes: ["ORGANIZATION", "TEXT"] as FieldType[],
                },
                {
                    inputCode: "contactIdField",
                    label: "Contact ID Field",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.INTEGER],
                    acceptedFieldTypes: ["CONTACT", "TEXT"] as FieldType[],
                },
                {
                    inputCode: "roleIdField",
                    label: "Role ID Field",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.INTEGER],
                    acceptedFieldTypes: ["ROLE", "TEXT"] as FieldType[],
                },
                {
                    inputCode: "contactRoleFieldRefs",
                    label: "Contact role field references (JSON)",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.JSON, AnswerType.INTEGER],
                },
            ],
        },
        ADD_CONTACT_ROLES: {
            actionCode: "ADD_CONTACT_ROLES",
            displayName: "Add Contact Roles",
            description:
                "Add multiple organization contact roles from ORG_CONTACT_ROLE form fields and/or contact role field references",
            inputs: [
                {
                    inputCode: "organizationIdField",
                    label: "Organization ID Field",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.INTEGER],
                    acceptedFieldTypes: ["ORGANIZATION", "TEXT"] as FieldType[],
                },
                {
                    inputCode: "contactRoleFieldRefs",
                    label: "Contact role field references",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.JSON, AnswerType.INTEGER],
                },
            ],
        },
        CREATE_ORG: {
            actionCode: "CREATE_ORG",
            displayName: "Create Organization",
            description: "Create a new organization",
            inputs: [
                {
                    inputCode: "acctNameField",
                    label: "Organization Name Field",
                    type: "text",
                    required: true,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "acctTitleField",
                    label: "Organization Title Field",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "acctNumberField",
                    label: "Organization Number Field",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "orgTypeIdField",
                    label: "Organization Type ID Field",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.INTEGER],
                    acceptedFieldTypes: ["ORG_TYPE", "TEXT"] as FieldType[],
                },
                {
                    inputCode: "primeContactField",
                    label: "Primary Contact ID Field",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.INTEGER],
                    acceptedFieldTypes: ["CONTACT", "TEXT"] as FieldType[],
                },
                {
                    inputCode: "commentField",
                    label: "Comment Field",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "contactRoleFieldRefs",
                    label: "Contact role field references",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.JSON, AnswerType.INTEGER],
                },
                {
                    inputCode: "actionConfig",
                    label: "Action config",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.JSON],
                },
                {
                    inputCode: "adminAddress",
                    label: "Administrative Address",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "adminCity",
                    label: "Administrative City",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "adminState",
                    label: "Administrative State",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "adminZip",
                    label: "Administrative Zip",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "adminCountry",
                    label: "Administrative Country",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "adminPhone",
                    label: "Administrative Phone",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "adminFax",
                    label: "Administrative Fax",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "billingAddress",
                    label: "Billing Address",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "billingCity",
                    label: "Billing City",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "billingState",
                    label: "Billing State",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "billingZip",
                    label: "Billing Zip",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "billingCountry",
                    label: "Billing Country",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "billingPhone",
                    label: "Billing Phone",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "billingFax",
                    label: "Billing Fax",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
            ],
        },
        EDIT_ORG: {
            actionCode: "EDIT_ORG",
            displayName: "Edit Organization",
            description: "Update an existing organization from form data.",
            inputs: [
                {
                    inputCode: "organizationIdField",
                    label: "Organization ID Field",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.INTEGER],
                    acceptedFieldTypes: ["ORGANIZATION", "TEXT"] as FieldType[],
                },
                {
                    inputCode: "acctNameField",
                    label: "Organization Name Field",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "acctTitleField",
                    label: "Organization Title Field",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "acctNumberField",
                    label: "Organization Number Field",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "orgTypeIdField",
                    label: "Organization Type ID Field",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.INTEGER],
                    acceptedFieldTypes: ["ORG_TYPE", "TEXT"] as FieldType[],
                },
                {
                    inputCode: "primeContactField",
                    label: "Primary Contact ID Field",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.INTEGER],
                    acceptedFieldTypes: ["CONTACT", "TEXT"] as FieldType[],
                },
                {
                    inputCode: "commentField",
                    label: "Comment Field",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "contactRoleFieldRefs",
                    label: "Contact role field references",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.JSON, AnswerType.INTEGER],
                },
                {
                    inputCode: "adminAddress",
                    label: "Administrative Address",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "adminCity",
                    label: "Administrative City",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "adminState",
                    label: "Administrative State",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "adminZip",
                    label: "Administrative Zip",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "adminCountry",
                    label: "Administrative Country",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "adminPhone",
                    label: "Administrative Phone",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "adminFax",
                    label: "Administrative Fax",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "billingAddress",
                    label: "Billing Address",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "billingCity",
                    label: "Billing City",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "billingState",
                    label: "Billing State",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "billingZip",
                    label: "Billing Zip",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "billingCountry",
                    label: "Billing Country",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "billingPhone",
                    label: "Billing Phone",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "billingFax",
                    label: "Billing Fax",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
            ],
        },
        ADD_CANDIDATE: {
            actionCode: "ADD_CANDIDATE",
            displayName: "Add Election Candidate",
            description:
                "Upsert a candidate for the election position selected on the form. The form must include an ELECTION_POSITION field. Map campaign brief and photo below.",
            inputs: [
                {
                    inputCode: "campaignBriefField",
                    label: "Campaign Brief Field",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.STRING, AnswerType.LARGE_STRING],
                },
                {
                    inputCode: "photoUrlField",
                    label: "Photo URL Field",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.LARGE_STRING],
                    acceptedFieldTypes: ["IMAGE"],
                },
            ],
        },
        ASSIGN_LIVE_25_DRAFT_EVENT: {
            actionCode: "ASSIGN_LIVE_25_DRAFT_EVENT",
            displayName: "Assign 25Live Draft Event",
            description:
                "Promote the embedded 25Live event form's draft event to Tentative and activate its reservations. Applies only to forms that include a LIVE_25_EVENT_FORM field. Optionally use 25Live assignment policy for location and resource approvals, or rely on the stage's object approvals instead.",
            inputs: [
                {
                    inputCode: "useAssignmentPolicy",
                    label: "Use Assignment Policy",
                    type: "checkbox",
                    required: false,
                },
            ] as Workflow.SystemActionInput[],
        },
        ADD_CATEGORIES: {
            actionCode: "ADD_CATEGORIES",
            displayName: "Add Categories to Event",
            description: "Add categories to an event",
            inputs: [
                {
                    inputCode: "eventIdField",
                    label: "Event ID Field",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.INTEGER],
                },
                {
                    inputCode: "booleanField",
                    label: "Boolean Field Reference",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.BOOLEAN],
                },
            ],
        },
        ADD_CUSTOM_ATTRIBUTES: {
            actionCode: "ADD_CUSTOM_ATTRIBUTES",
            displayName: "Add Custom Attributes to Event",
            description: "Add custom attribute values to an event",
            inputs: [
                {
                    inputCode: "eventIdField",
                    label: "Event ID Field",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.INTEGER],
                },
            ],
        },
        ADD_ORG_CATEGORIES: {
            actionCode: "ADD_ORG_CATEGORIES",
            displayName: "Add Categories to Organization",
            description: "Add categories to an organization",
            inputs: [
                {
                    inputCode: "booleanField",
                    label: "Boolean Field Reference",
                    type: "text",
                    required: false,
                    acceptedAnswerTypes: [AnswerType.BOOLEAN],
                },
            ],
        },
        ADD_ORG_CUSTOM_ATTRIBUTES: {
            actionCode: "ADD_ORG_CUSTOM_ATTRIBUTES",
            displayName: "Add Custom Attributes to Organization",
            description: "Add custom attribute values to an organization",
        },
    } satisfies Record<string, Workflow.SystemAction>;

    static getSystemActionDisplayName(actionCode: string): string | undefined {
        const meta = WorkflowService.SYSTEM_ACTIONS[actionCode as keyof typeof WorkflowService.SYSTEM_ACTIONS];
        return meta?.displayName;
    }

    /**
     * Get all workflow processes
     */
    public static async getWorkflowProcesses(enabled?: boolean): Promise<Workflow.WorkflowProcess[]> {
        const params = new URLSearchParams();
        if (S25Util.isDefined(enabled)) {
            params.set("enabled", enabled.toString());
        }
        const processes =
            (await DataAccess.get<Workflow.WorkflowProcess[]>(`/workflow/processes.json?${params.toString()}`)) ?? [];
        processes.sort(S25Util.shallowSort("workflowProcessName", true));
        return processes;
    }

    /**
     * Get workflow process by ID
     */
    @Cache({ targetName: "WorkflowService", immutable: true })
    public static async getWorkflowProcess(workflowProcessId: number): Promise<Workflow.WorkflowProcess> {
        return await DataAccess.get<Workflow.WorkflowProcess>(`/workflow/process/${workflowProcessId}.json`);
    }

    /**
     * Create a new workflow process
     */
    public static createWorkflowProcess(workflowProcess: Workflow.WorkflowProcess): Promise<ResponseMessage> {
        return DataAccess.post<ResponseMessage>("/workflow/process.json", workflowProcess);
    }

    /**
     * Update an existing workflow process
     */
    public static updateWorkflowProcess(
        workflowProcessId: number,
        workflowProcess: Workflow.WorkflowProcess,
    ): Promise<ResponseMessage> {
        return DataAccess.put<ResponseMessage>(`/workflow/process/${workflowProcessId}.json`, workflowProcess);
    }

    /**
     * Delete a workflow process
     */
    public static deleteWorkflowProcess(workflowProcessId: number): Promise<ResponseMessage> {
        return DataAccess.delete<ResponseMessage>(`/workflow/process/${workflowProcessId}.json`);
    }

    /**
     * Get the reviewers for a workflow process stage.
     * Returns R25User objects with contact info (including email) for users who have a 25Live login.
     */
    public static async getStageReviewers(
        processInstanceId: number,
        stageId: number,
    ): Promise<Workflow.WorkflowR25User[]> {
        return (
            (await DataAccess.get<Workflow.WorkflowR25User[]>(
                `/workflow/process-instance/${processInstanceId}/stage/${stageId}/reviewers.json`,
            )) ?? []
        );
    }

    /**
     * Get workflow process instance status with contact approvals
     */
    @Cache({ targetName: "WorkflowService", immutable: true })
    public static getWorkflowStatus(processInstanceId: number): Promise<Workflow.ProcessInstanceStatus> {
        return DataAccess.get(
            DataAccess.injectCaller(
                `/workflow/process-instance/${processInstanceId}/status.json`,
                "WorkflowService.getWorkflowStatus",
            ),
        );
    }

    /**
     * Everything the workflow diagram shows for one process instance (status plus spawned forms, AI outcomes,
     * resolved action inputs, and execution results). Not cached: the diagram's Refresh must always reload.
     */
    public static getWorkflowDiagram(processInstanceId: number): Promise<Workflow.ProcessInstanceDiagram> {
        return DataAccess.get(
            DataAccess.injectCaller(
                `/workflow/process-instance/${processInstanceId}/diagram.json`,
                "WorkflowService.getWorkflowDiagram",
            ),
        );
    }

    /**
     * Active workflow todos assigned to the current user (nav / badge).
     */
    public static async getAssignedWorkflowCount(): Promise<number> {
        const loggedIn = await UserprefService.getLoggedIn();
        if (loggedIn) {
            const res = await DataAccess.get<Workflow.AssignedWorkflowCountResponse>("/workflow/assigned-count.json");
            return res?.count ?? 0;
        }
        return 0;
    }

    /**
     * One page of every workflow item ever assigned to the current user, filtered, sorted, and faceted server-side.
     */
    public static async getAssignedWorkflowPage(
        filters: Workflow.AssignedWorkflowFilters,
        page: number,
        size: number,
    ): Promise<Workflow.AssignedWorkflowPage> {
        const params = DataAccess.params({
            scope: "all" satisfies Workflow.AssignedWorkflowScope,
            page,
            size,
            keyword: filters.keyword?.trim() || undefined,
            status: filters.status,
            orgId: filters.organizationId,
            form: filters.formName,
            submitterContId: filters.submitterContId,
            from: filters.submittedFrom ? S25Date.toS25ISODateStr(filters.submittedFrom) : undefined,
            to: filters.submittedTo ? S25Date.toS25ISODateStr(filters.submittedTo) : undefined,
            actionable: filters.actionableOnly || undefined,
            sortDirection: filters.sortAscending ? "asc" : "desc",
        });
        const response = await DataAccess.get<Workflow.AssignedWorkflowPage>(`/workflow/assigned.json?${params}`);
        return {
            totalCount: response?.totalCount ?? 0,
            facets: response?.facets,
            items: (response?.items ?? []).map((item) => ({
                ...item,
                itemTypeName: this.getItemTypeName(item.itemTypeId),
                submitterName: item.contactName || `Contact ${item.contId}`,
            })),
        };
    }

    public static getItemTypeName(itemTypeId?: number): string {
        switch (itemTypeId) {
            case Workflow.WORKFLOW_INGESTION_SUBMISSION_OBJECT_TYPE:
                return "Ingestion Submission";
            default:
                return "Unknown";
        }
    }

    /**
     * Get workflow history for a submission
     */
    public static getWorkflowHistory(itemTypeId: number, itemId: number): Promise<Task.DBTodo[]> {
        const params = new URLSearchParams();
        params.set("itemTypeId", itemTypeId.toString());
        params.set("itemId", itemId.toString());
        return DataAccess.get<Task.DBTodo[]>(`/workflow/history.json?${params.toString()}`);
    }

    /**
     * Get requested changes from todo comments for a workflow item
     */
    public static getRequestedChanges(itemTypeId: number, itemId: number): Promise<Task.DBTodo[]> {
        const params = new URLSearchParams();
        params.set("itemTypeId", itemTypeId.toString());
        params.set("itemId", itemId.toString());
        return DataAccess.get<Task.DBTodo[]>(`/workflow/requested-changes.json?${params.toString()}`);
    }

    /**
     * Request changes for a workflow submission using todo ID
     */
    public static requestChangesFromTodo(todoId: number, requestedChanges: string): Promise<ResponseMessage> {
        return DataAccess.post<ResponseMessage>(`/workflow/todo/${todoId}/request-changes.json`, requestedChanges);
    }

    /**
     * User-visible text for non-fatal workflow action warnings returned on success.
     */
    public static formatResponseWarnings(response: ResponseMessage | undefined): string | undefined {
        if (!response?.warnings?.length) {
            return undefined;
        }
        return response.warnings.join("\n\n");
    }

    private static notifyWorkflowWarnings(response: ResponseMessage | undefined): void {
        const warningText = WorkflowService.formatResponseWarnings(response);
        if (warningText) {
            AlertService.alert(warningText);
        }
    }

    /**
     * Approve a workflow submission using todo ID
     */
    public static async approveSubmissionFromTodo(todoId: number): Promise<ResponseMessage> {
        const results = await TaskService.changeNonEventTask({ itemId: todoId.toString() }, Task.States.Completed);
        if (results && !Array.isArray(results) && results.messageCode) {
            WorkflowService.notifyWorkflowWarnings(results);
            return results;
        }
        if (Array.isArray(results) && results.length > 0 && "success" in results[0]) {
            return { message: "Submission approved successfully", messageCode: "success" };
        } else {
            throw new Error("Failed to approve submission");
        }
    }

    /**
     * Deny a workflow submission using todo ID
     */
    public static async denySubmissionFromTodo(todoId: number): Promise<ResponseMessage> {
        const results = await TaskService.changeNonEventTask({ itemId: todoId.toString() }, Task.States.Denied);
        if (results && !Array.isArray(results) && results.messageCode) {
            return results;
        }
        if (Array.isArray(results) && results.length > 0 && "success" in results[0]) {
            return { message: "Submission denied successfully", messageCode: "success" };
        } else {
            throw new Error("Failed to deny submission");
        }
    }

    /**
     * Dismiss a workflow queue item for the current assignee (todo cancelled; does not deny the submission).
     */
    public static async dismissSubmissionFromTodo(todoId: number): Promise<ResponseMessage> {
        const results = await TaskService.changeNonEventTask({ itemId: todoId.toString() }, Task.States.Cancelled);
        if (results && !Array.isArray(results) && results.messageCode) {
            return results;
        }
        if (Array.isArray(results) && results.length > 0 && "success" in results[0]) {
            return { message: "Submission dismissed from your queue", messageCode: "success" };
        } else {
            throw new Error("Failed to dismiss submission");
        }
    }

    /**
     * Get workflow action history for a date range
     */
    public static async getActionHistory(startDate: Date, endDate: Date): Promise<Workflow.WorkflowActionHistory[]> {
        const formattedStartDate = S25Datefilter.transform(startDate, S25Dateformat.getISODateTimeFormat());
        const formattedEndDate = S25Datefilter.transform(endDate, S25Dateformat.getISODateTimeFormat());

        const encodedStartDate = encodeURIComponent(formattedStartDate);
        const encodedEndDate = encodeURIComponent(formattedEndDate);

        return DataAccess.get<Workflow.WorkflowActionHistory[]>(
            `/workflow/action-history.json?startDate=${encodedStartDate}&endDate=${encodedEndDate}`,
        );
    }

    /**
     * Get a workflow item with linked forms populated (children and parent chain)
     */
    public static async getWorkflowItemWithLinkedForms(
        itemTypeId: number,
        itemId: number,
    ): Promise<Workflow.WorkflowItem> {
        return await DataAccess.get<Workflow.WorkflowItem>(`/workflow/item/${itemTypeId}/${itemId}/linked-forms.json`);
    }

    /**
     * Cancel a workflow - only available to submitters
     */
    public static cancelWorkflow(itemTypeId: number, itemId: number): Promise<ResponseMessage> {
        return DataAccess.post<ResponseMessage>(`/workflow/item/${itemTypeId}/${itemId}/cancel.json`, null);
    }

    /**
     * Reset a workflow item - deletes all X-table entries and reinitializes using current workflow definition
     * Only available to admins
     */
    public static resetWorkflow(itemTypeId: number, itemId: number): Promise<ResponseMessage> {
        return DataAccess.post<ResponseMessage>(`/workflow/item/${itemTypeId}/${itemId}/reset.json`, null);
    }
}
